import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { RowSelectionState, PopupButton } from 'design-system-web';
import { bulkUpdateServiceRequestStatusMutation } from 'graphql-queries';
import { ServiceRequestStatusEnum } from 'domains/workOrder/serviceRequest/components/types/serviceRequestStatus';
import { getServiceRequestStatusLabel } from 'domains-work-order-type-contracts';

const SERVICE_REQUEST_SELECTION_LIMIT = 100;

type Props = {
    rowSelection?: RowSelectionState;
    refetch?: () => void;
};

export function BulkStatusUpdateAction({ rowSelection, refetch }: Props) {
    const { t } = useTranslation();
    const [bulkUpdateServiceRequestStatus] = useMutation(bulkUpdateServiceRequestStatusMutation);

    const handleStatusUpdate = async (status: string) => {
        if (!status || !rowSelection) return;

        const selectedWorkOrderIds = Object.entries(rowSelection)
            .filter(([_, isSelected]) => isSelected)
            .map(([workOrderId]) => workOrderId);

        if (selectedWorkOrderIds.length === 0) {
            await window.Toast.show(
                t('web.admin.serviceRequest.bulkActions.updateStatus.noSelection')
            );
            return;
        }

        if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
            await window.Toast.show(
                t(
                    'web.admin.serviceRequest.bulkActions.updateStatus.limitExceeded',
                    {
                        serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
                        selectedWorkOrderCount: selectedWorkOrderIds.length,
                    }
                ),
                5000
            );
            return;
        }

        try {
            await window.Alert.confirm({
                title: t('web.admin.serviceRequest.bulkActions.updateStatus.confirm.title'),
                message: t(
                    'web.admin.serviceRequest.bulkActions.updateStatus.confirm.message',
                    {
                        count: selectedWorkOrderIds.length,
                        status: getServiceRequestStatusLabel(status),
                    }
                ),
                confirmText: t('web.admin.serviceRequest.bulkActions.updateStatus.confirm.button'),
                cancelText: t('web.admin.serviceRequest.bulkActions.updateStatus.cancel.button'),
            });
        } catch {
            return;
        }

        window.Alert.loading({
            title: t('web.admin.serviceRequest.bulkActions.updateStatus.inProgress'),
            disableCloseOnBackgroundClick: true,
        });

        try {
            const result = await bulkUpdateServiceRequestStatus({
                variables: {
                    serviceRequestIds: selectedWorkOrderIds,
                    status,
                },
            });

            const { successCount, failureCount } =
                result.data?.bulkUpdateServiceRequestStatus || {
                    successCount: 0,
                    failureCount: selectedWorkOrderIds.length,
                };

            window.Alert.hide();

            if (failureCount === 0) {
                await window.Toast.show(
                    t('web.admin.serviceRequest.bulkActions.updateStatus.success', {
                        count: successCount,
                    })
                );
            } else if (successCount === 0) {
                await window.Toast.show(
                    t('web.admin.serviceRequest.bulkActions.updateStatus.error')
                );
            } else {
                await window.Toast.show(
                    t('web.admin.serviceRequest.bulkActions.updateStatus.partial', {
                        successCount,
                        failureCount,
                    }),
                    5000
                );
            }

            if (refetch) {
                refetch();
            }
        } catch {
            window.Alert.hide();
            await window.Toast.show(
                t('web.admin.serviceRequest.bulkActions.updateStatus.error')
            );
        }
    };

    const statusMenuItems = [
        ServiceRequestStatusEnum.Created,
        ServiceRequestStatusEnum.Accepted,
        ServiceRequestStatusEnum.InProgress,
        ServiceRequestStatusEnum.OnHold,
        ServiceRequestStatusEnum.Complete,
        ServiceRequestStatusEnum.Closed,
        ServiceRequestStatusEnum.Declined,
        ServiceRequestStatusEnum.Cancelled,
    ].map((status) => ({
        label: getServiceRequestStatusLabel(status) || status,
        onSelect: () => handleStatusUpdate(status),
    }));

    return (
        <PopupButton
            variant="secondary"
            size="large"
            items={statusMenuItems}
            testId="bulk-status-update-button"
        >
            {t('web.admin.serviceRequest.bulkActions.updateStatus.selectStatus')}
        </PopupButton>
    );
}